import React, { useEffect, useState } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import styled, { createGlobalStyle } from "styled-components";
import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { Spin, message, Checkbox, Modal } from "antd";
import dayjs from "dayjs";
import { CALL_FEATCH_API, CALL_API } from "common/API";
import { useSelector } from "react-redux";
import { getClientid, getUserInfo } from "redux/selectors";

const SuppressLayoutScroll = createGlobalStyle`
  .ant-layout-content {
    padding: 0 !important;
  }
`;
const PageLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 60px);
  background: #fff;
  color: #262626;
  overflow: hidden;
  padding: 24px 24px 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const TopSection = styled.div`
  flex-shrink: 0;
`;
const DescriptionScroll = styled.div`
  flex: 1;          
  overflow-y: auto;
  padding-right: 6px;
  min-height: 0;   

  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 10px;
  }
  scrollbar-width: thin;
  scrollbar-color: #d9d9d9 transparent;
`;

const FileFooter = styled.div`
  flex-shrink: 0;
  border-top: 1px solid #f0f0f0;
  padding: 16px 0 20px;
  background: #fff;
`;

const FilesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

// ─────────────────────────────────────────────
// Styled Components
// ─────────────────────────────────────────────

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  .crumb-link {
    color: #8c8c8c;
    cursor: pointer;
    font-weight: 400;
    &:hover { color: #1677ff; }
  }
  .separator { color: #8c8c8c; font-size: 12px; }
  .crumb-current { color: #262626; font-weight: 500; }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #262626;
  margin: 0;
  line-height: 1.4;
`;

const RemoveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  color: #1677ff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 0;
  white-space: nowrap;
  flex-shrink: 0;
  margin-top: 4px;
  &:hover { color: #0958d9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
`;

const LevelBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 400;
  background: ${({ level }) => level === "Important" ? "#e6e3e3" : "#f6ffed"};
  color: ${({ level }) => level === "Important" ? "#444141" : "#389e0d"};
  border: 1px solid ${({ level }) => level === "Important" ? "#e6e3e3" : "#b7eb8f"};
`;

const MetaText = styled.span`
  font-size: 13px;
  color: #8c8c8c;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #f0f0f0;
  margin: 12px 0;
`;

const Description = styled.div`
  font-size: 14px;
  color: #262626;
  line-height: 1.8;
  padding-bottom: 12px;
  p { margin: 0 0 8px; }
  ol, ul { margin: 8px 0 8px 20px; padding: 0; }
  li { margin-bottom: 4px; }
`;

// ─────────────────────────────────────────────
// File Card
// ─────────────────────────────────────────────

const FileCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 180px;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  padding: 14px 16px 12px;
  background: #fafafa;
  cursor: ${({ $loading }) => ($loading ? "not-allowed" : "pointer")};
  transition: border-color 0.2s, box-shadow 0.2s;
  position: relative;
  overflow: hidden;
  gap: 10px;
  &:hover {
    border-color: ${({ $loading }) => ($loading ? "#e8e8e8" : "#d0d0d0")};
    box-shadow: ${({ $loading }) => ($loading ? "none" : "0 2px 8px rgba(0,0,0,0.07)")};
  }
`;

const CardFileName = styled.p`
  font-size: 13px;
  font-weight: 500;
  color: #262626;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardBottom = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CardLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.78);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
`;

const ExtLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #595959;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const PdfFileIcon = () => (
    <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
        <path d="M5 2h14l9 9v19a2 2 0 01-2 2H5a2 2 0 01-2-2V4a2 2 0 012-2z"
            fill="#fff0f0" stroke="#e53935" strokeWidth="1.5" />
        <path d="M19 2v9h9" stroke="#e53935" strokeWidth="1.5" fill="none" />
        <text x="5" y="25" fontSize="7.5" fontWeight="800" fill="#e53935" fontFamily="Arial, sans-serif">PDF</text>
    </svg>
);

const DocFileIcon = () => (
    <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
        <path d="M5 2h14l9 9v19a2 2 0 01-2 2H5a2 2 0 01-2-2V4a2 2 0 012-2z"
            fill="#e6f4ff" stroke="#1677ff" strokeWidth="1.5" />
        <path d="M19 2v9h9" stroke="#1677ff" strokeWidth="1.5" fill="none" />
        <line x1="8" y1="18" x2="22" y2="18" stroke="#1677ff" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="8" y1="22" x2="17" y2="22" stroke="#1677ff" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);

// ─────────────────────────────────────────────
// Confirm Modal
// ─────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 32px 36px 28px;
  width: 460px;
  max-width: 90vw;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.15);
`;

const ModalText = styled.p`
  font-size: 15px;
  color: #262626;
  line-height: 1.6;
  margin: 0 0 20px;
  strong { font-weight: 700; }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 28px;
`;

const BtnNo = styled.button`
  padding: 8px 0;
  border-radius: 8px;
  border: 1.5px solid #262626;
  background: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  color: #262626;
  width: 120px;
  &:hover { background: #f5f5f5; }
`;

const BtnYes = styled.button`
  padding: 8px 0;
  border-radius: 8px;
  border: none;
  background: #00685F;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  color: #ffffff;
  width: 120px;
  &:hover { background: #00685F; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const PreviewFrame = styled.iframe`
  width: 100%;
  height: 75vh;
  border: none;
  border-radius: 4px;
`;

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

const AnnouncementDetail = () => {
    const history = useHistory();
    const { id } = useParams();
    const location = useLocation();
    const viewOnly = location?.state?.viewOnly || false;

    const [detailData, setDetailData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [removeLoading, setRemoveLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [dontShowAgain, setDontShowAgain] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState("");
    const [previewFileName, setPreviewFileName] = useState("");
    const [previewLoadingId, setPreviewLoadingId] = useState(null);

    const userInfo = useSelector(getUserInfo);
    const clientId = useSelector(getClientid);

    useEffect(() => {
        if (id) fetchDetail();
    }, [id]);

    const fetchDetail = async () => {
        try {
            setLoading(true);
            const response = await CALL_FEATCH_API(
                `insyts-user-dashboard/getannouncement?id=${id}`,
                "get"
            );
            if (response?.status === "SUCCESS") {
                const data = response?.response?.data;
                setDetailData(Array.isArray(data) ? data[0] : data);
            }
        } catch (error) {
            message.error("Announcement detail fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handlePreviewFile = async (file, index) => {
        const documentId = file?.documentId || file?.document_id;
        if (!documentId) {
            message.info("Preview not available for this file");
            return;
        }
        const cardKey = String(documentId);
        try {
            setPreviewLoadingId(cardKey);

            const payload = {
                method: "preview-announcement-document",
                documentId: String(documentId),
                announcementId: String(id),
            };

            // Pass HR policy fields if applicable
            if (file?.isHrPolicy === true && file?.policyId) {
                payload.isPolicy = true;
                payload.policyId = String(file.policyId);
            }

            const res = await CALL_API(`document-list/${clientId}`, "post", payload);
            const url = res?.url || res?.previewUrl || res?.signedUrl || res?.data?.url;
            if (!url) { message.error("Preview URL not available"); return; }

            const fileName =
                typeof file === "string"
                    ? file.split("/").pop()
                    : file?.fileName || file?.name || `File ${index + 1}`;

            setPreviewUrl(url);
            setPreviewFileName(fileName);
            setPreviewOpen(true);
        } catch {
            message.error("Failed to load preview");
        } finally {
            setPreviewLoadingId(null);
        }
    };

    const handleConfirmYes = async () => {
        try {
            setRemoveLoading(true);
            await CALL_API("insyts-user-dashboard/updateannouncement", "patch", {
                id: Number(id),
                removeStatus: true,
                removedUserId: userInfo?.userid,
            });
            setConfirmOpen(false);
            message.success("Announcement removed successfully");
            history.push("/announcement");
        } catch {
            message.error("Failed to remove announcement");
        } finally {
            setRemoveLoading(false);
        }
    };

    if (loading) {
        return (
            <PageLayout style={{ alignItems: "center", justifyContent: "center" }}>
                <Spin size="large" />
            </PageLayout>
        );
    }

    const { title, level, createdby, createdat, announcement_description, attacheddocument } =
        detailData || {};

    const formattedDate = createdat
        ? dayjs(createdat).format("DD[th] MMM, YYYY ; hh:mm A")
        : "-";

    let attachments = [];
    if (attacheddocument) {
        if (typeof attacheddocument === "string") {
            try { attachments = JSON.parse(attacheddocument); } catch { attachments = []; }
        } else if (Array.isArray(attacheddocument)) {
            attachments = attacheddocument;
        } else {
            attachments = [attacheddocument];
        }
    }

    return (
        <PageLayout>
            <SuppressLayoutScroll />
            {/* ── Remove Confirm Modal ── */}
            {confirmOpen && (
                <ModalOverlay onClick={() => setConfirmOpen(false)}>
                    <ModalBox onClick={(e) => e.stopPropagation()}>
                        <ModalText>
                            Are you sure you want to <strong>remove</strong> this announcement from the list?
                        </ModalText>
                        <Checkbox
                            checked={dontShowAgain}
                            onChange={(e) => setDontShowAgain(e.target.checked)}
                            style={{ fontSize: 14, color: "#262626" }}
                        >
                            Don't show this again
                        </Checkbox>
                        <ModalFooter>
                            <BtnNo onClick={() => setConfirmOpen(false)}>No</BtnNo>
                            <BtnYes onClick={handleConfirmYes} loading ={removeLoading} disabled={removeLoading}>Yes</BtnYes>
                        </ModalFooter>
                    </ModalBox>
                </ModalOverlay>
            )}

            {/* ── File Preview Modal ── */}
            <Modal
                open={previewOpen}
                onCancel={() => { setPreviewOpen(false); setPreviewUrl(""); }}
                footer={null}
                title={previewFileName || "File Preview"}
                width="80vw"
                centered
                destroyOnClose
                bodyStyle={{ padding: "12px 0 0" }}
            >
                {previewUrl ? (
                    <PreviewFrame src={previewUrl} title={previewFileName} allow="fullscreen" />
                ) : (
                    <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
                        <Spin size="large" />
                    </div>
                )}
            </Modal>

            <TopSection>
                <Breadcrumb>
                    <span className="crumb-link" onClick={() => history.push("/announcement")}>
                        Announcement
                    </span>
                    <span className="separator">›</span>
                    <span className="crumb-current">Detail</span>
                </Breadcrumb>

                <Header>
                    <Title>{title || "—"}</Title>
                    {!viewOnly && (
                        <RemoveBtn onClick={() => setConfirmOpen(true)} disabled={removeLoading}>
                            <CloseOutlined style={{ fontSize: 11 }} /> Remove
                        </RemoveBtn>
                    )}
                </Header>

                <MetaRow>
                    {level && <LevelBadge level={level}>{level}</LevelBadge>}
                    <MetaText>
                        Published by: {createdby || "—"} &nbsp;|&nbsp; {formattedDate}
                    </MetaText>
                </MetaRow>

                <Divider />
            </TopSection>

            <DescriptionScroll>
                <Description>
                    {announcement_description ? (
                        announcement_description.startsWith("<") ? (
                            <div dangerouslySetInnerHTML={{ __html: announcement_description }} />
                        ) : (
                            <p>{announcement_description}</p>
                        )
                    ) : (
                        <p style={{ color: "#bbb" }}>No description provided.</p>
                    )}
                </Description>
            </DescriptionScroll>

            {attachments?.length > 0 && (
                <FileFooter>
                    <FilesGrid>
                        {attachments.map((file, i) => {
                            const fileName =
                                typeof file === "string"
                                    ? file.split("/").pop() || file
                                    : file?.fileName || file?.name || `File ${i + 1}`;
                            const nameWithoutExt = fileName.replace(/\.[^/.]+$/, "");
                            const ext = fileName.split(".").pop()?.toLowerCase() || "file";
                            const documentId = file?.documentId || file?.document_id;
                            const cardKey = String(documentId || i);
                            const isLoading = previewLoadingId === cardKey;
                            const isPdf = ext === "pdf";

                            return (
                                <FileCard
                                    key={i}
                                    $loading={isLoading}
                                    onClick={() => !isLoading && handlePreviewFile(file, i)}
                                >
                                    {isLoading && (
                                        <CardLoadingOverlay>
                                            <LoadingOutlined style={{ fontSize: 18, color: "#1677ff" }} />
                                        </CardLoadingOverlay>
                                    )}
                                    <CardFileName title={fileName}>{nameWithoutExt}</CardFileName>
                                    <CardBottom>
                                        {isPdf ? <PdfFileIcon /> : <DocFileIcon />}
                                        <ExtLabel>{ext}</ExtLabel>
                                    </CardBottom>
                                </FileCard>
                            );
                        })}
                    </FilesGrid>
                </FileFooter>
            )}

        </PageLayout>
    );
};

export default AnnouncementDetail;